/**
 * Email-template hooks (Settings → Templates, gap #4).
 *
 * Org-scoped CRUD over the canned-reply table. Internal staff only —
 * the API 403s for clients so the hooks fail gracefully on the rare
 * misroute.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { emailTemplatesApi } from '@beacon/api-client';
import type {
  EmailTemplate,
  EmailTemplateCreateInput,
  EmailTemplateUpdateInput,
} from '@beacon/core';

import { beaconClient } from '@/lib/api/client.js';

export const emailTemplateKeys = {
  all: ['email-templates'] as const,
  list: () => [...emailTemplateKeys.all, 'list'] as const,
};

export function useEmailTemplates() {
  return useQuery({
    queryKey: emailTemplateKeys.list(),
    queryFn: async (): Promise<EmailTemplate[]> => {
      const res = await emailTemplatesApi.list(beaconClient);
      return res.templates;
    },
    // Templates change rarely — minute-stale is plenty.
    staleTime: 60_000,
  });
}

export function useCreateEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmailTemplateCreateInput) =>
      emailTemplatesApi.create(beaconClient, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailTemplateKeys.list() });
      toast.success('Template created');
    },
    onError: (err: Error) => {
      toast.error("Couldn't create template", { description: err.message });
    },
  });
}

interface UpdateVars {
  id: string;
  input: EmailTemplateUpdateInput;
}

export function useUpdateEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateVars) =>
      emailTemplatesApi.update(beaconClient, id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailTemplateKeys.list() });
      toast.success('Template saved');
    },
    onError: (err: Error) => {
      toast.error("Couldn't save template", { description: err.message });
    },
  });
}

export function useDeleteEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => emailTemplatesApi.remove(beaconClient, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailTemplateKeys.list() });
      toast.success('Template deleted');
    },
    onError: (err: Error) => {
      toast.error("Couldn't delete template", { description: err.message });
    },
  });
}
